import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiServer, FiSave } from "react-icons/fi";

import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { withAuth } from "../../../components/HOC/withAuth";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Select from "../../../components/BaseUI/Select";
import Label from "../../../components/BaseUI/Label";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import Lozenge from "../../../components/BaseUI/Lozenge";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const CreateDeviceModel = () => {
  const [formData, setFormData] = useState({
    code: "",
    price: 0,
    name: "",
    description: "",
    status: "active",
    usedInventory: [],
  });
  const [inventoryItems, setInventoryItems] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { getToken } = useAuth();

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.MODELS"), url: "/device-models" },
    { label: translate("UI.ADD_DEVICE_MODEL") },
  ];

  const handleInputChange = (value, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUsedInventoryChange = (index, field, value) => {
    setFormData((prev) => {
      const next = [...(prev.usedInventory || [])];
      next[index] = {
        ...(next[index] || { inventoryItemId: "", quantity: 0 }),
        [field]: value,
      };
      return { ...prev, usedInventory: next };
    });
  };

  const addUsedInventoryRow = () => {
    setFormData((prev) => ({
      ...prev,
      usedInventory: [
        ...(prev.usedInventory || []),
        { inventoryItemId: "", quantity: 0 },
      ],
    }));
  };

  const removeUsedInventoryRow = (index) => {
    setFormData((prev) => {
      const next = [...(prev.usedInventory || [])];
      next.splice(index, 1);
      return { ...prev, usedInventory: next };
    });
  };

  const fetchInventory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = res.data?.items || res.data || [];
      setInventoryItems(Array.isArray(data) ? data : []);
    } catch {
      setInventoryItems([]);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  const handleStatusChange = (status) => {
    setFormData((prev) => ({
      ...prev,
      status,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log(formData);
    if (!formData.code || !formData.name) {
      setToasts([
        {
          description: translate("UI.FILL_REQUIRED_FIELDS"),
          id: Date.now(),
          title: "Uyarı!",
          appearance: "warning",
        },
      ]);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/api/device-models`,
        { ...formData },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        navigate("/device-models");
      }
    } catch (error) {
      console.error("Model oluşturma hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message || translate("UI.MODEL_CREATE_ERROR"),
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  return (
    <AppPage title={translate("UI.ADD_DEVICE_MODEL")} breadcrumbs={breadcrumbs}>
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card shadow-sm">
            <div className="card-body">
              {/* Model Bilgileri */}
              <div className="section-container">
                <div className="section-header">
                  <div className="section-icon">
                    <FiServer className="text-primary" />
                  </div>
                  <div className="section-title">
                    <h5>{translate("UI.MODEL_INFORMATION")}</h5>
                  </div>
                </div>

                <div className="section-content">
                    <form onSubmit={handleSubmit}>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <Input
                            label={translate("UI.MODEL_NAME")}
                            name="name"
                            value={formData.name}
                            onChange={(value) =>
                              handleInputChange(value, "name")
                            }
                            required
                            helpText={translate("UI.MODEL_NAME_DESC")}
                          />
                        </div>
                        <div className="col-md-6">
                          <Input
                            label={translate("UI.MODEL_CODE")}
                            name="code"
                            value={formData.code}
                            onChange={(value) =>
                              handleInputChange(value, "code")
                            }
                            required
                            helpText={translate("UI.MODEL_CODE_DESC")}
                          />
                        </div>

                        <div className="col-md-6">
                          <Label>{translate("UI.STATUS")}</Label>
                          <div className="form-control-container">
                            <Dropdown
                              trigger={
                                <div className="d-flex align-items-center justify-content-between p-2">
                                  <Lozenge
                                    appearance={
                                      formData.status === "active"
                                        ? "success-subtle"
                                        : "danger-subtle"
                                    }
                                  >
                                    {translate(
                                      `UI.${formData.status.toUpperCase()}`,
                                    )}
                                  </Lozenge>
                                </div>
                              }
                            >
                              <DropdownContent>
                                <DropdownItem
                                  onClick={() => handleStatusChange("active")}
                                  active={formData.status === "active"}
                                >
                                  <Lozenge appearance="success-subtle">
                                    {translate("UI.ACTIVE")}
                                  </Lozenge>
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() => handleStatusChange("inactive")}
                                  active={formData.status === "inactive"}
                                >
                                  <Lozenge appearance="danger-subtle">
                                    {translate("UI.INACTIVE")}
                                  </Lozenge>
                                </DropdownItem>
                              </DropdownContent>
                            </Dropdown>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <Input
                            type="number"
                            label={`${translate("UI.PRICE")} (USD)`}
                            name="price"
                            value={formData.price}
                            onChange={(value) =>
                              handleInputChange(value, "price")
                            }
                            required
                            helpText={translate("UI.PRICE_DESC")}
                          />
                        </div>
                        <div className="col-12">
                          <Input
                            type="textarea"
                            label={translate("UI.DESCRIPTION")}
                            name="description"
                            value={formData.description}
                            onChange={(value) =>
                              handleInputChange(value, "description")
                            }
                            rows={4}
                            helpText={translate("UI.MODEL_DESCRIPTION_DESC")}
                          />
                        </div>
                        <div className="col-12">
                          <Label>Kullanılan Envanter</Label>
                          <div className="specifications-container">
                            {(formData.usedInventory || []).map(
                              (row, index) => (
                                <div key={index} className="specification-item">
                                  <div className="row g-2 align-items-center">
                                    <div className="col-md-8">
                                      <Select
                                        label="Envanter Kalemi"
                                        unique
                                        options={inventoryItems.map((item) => ({
                                          value: item._id,
                                          label: `${item.name} (${item.code})`,
                                        }))}
                                        selected={
                                          row.inventoryItemId
                                            ? [row.inventoryItemId]
                                            : []
                                        }
                                        onSelected={(values) =>
                                          handleUsedInventoryChange(
                                            index,
                                            "inventoryItemId",
                                            values?.[0]?.value || "",
                                          )
                                        }
                                        placeholder="Envanter seçin"
                                      />
                                    </div>
                                    <div className="col-md-3">
                                      <Input
                                        type="number"
                                        label="Miktar"
                                        min={0}
                                        value={row.quantity}
                                        onChange={(value) =>
                                          handleUsedInventoryChange(
                                            index,
                                            "quantity",
                                            Number(value) || 0,
                                          )
                                        }
                                      />
                                    </div>
                                    <div className="col-md-1 d-flex align-items-end">
                                      <Button
                                        appearance="danger-subtle"
                                        onClick={() =>
                                          removeUsedInventoryRow(index)
                                        }
                                      >
                                        Sil
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ),
                            )}
                            <Button
                              appearance="subtle"
                              onClick={addUsedInventoryRow}
                            >
                              Envanter satırı ekle
                            </Button>
                          </div>
                        </div>
                      </div>
                    </form>
                </div>
              </div>

              {/* Butonlar */}
              <div className="form-actions p-3">
                <Button
                  appearance="danger-subtle"
                  onClick={() => navigate("/device-models")}
                >
                  {translate("UI.CANCEL")}
                </Button>
                <Button
                  appearance="primary-animated"
                  onClick={handleSubmit}
                  loading={loading}
                  icon={<FiSave />}
                >
                  {translate("UI.SAVE")}
                </Button>
              </div>
            </div>
          </div>
        </ContainerItem>
      </Container>

      <style>
        {`
                .section-container {
                    margin-bottom: 2rem;
                    padding: 1.5rem;
                    background: #fff;
                    border-radius: 8px;
                    border: 1px solid #e9ecef;
                }

                .section-container:last-child {
                    margin-bottom: 0;
                }

                .section-header {
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    gap: .3rem;
                    margin-bottom: 1.5rem;
                }

                .section-icon {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--bs-primary-bg-subtle);
                    border-radius: 8px;
                }

                .section-icon svg {
                    width: 24px;
                    height: 24px;
                }

                .section-title h5 {
                    font-size: 1.1rem;
                    margin: 0;
                    color: var(--bs-gray-900);
                }

                .section-content {
                    padding: 0 0.5rem;
                }

                .form-control-container {
                    background: #fff;
                    border: 1px solid #e2e5e8;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                }

                .form-control-container:hover {
                    border-color: var(--bs-primary);
                }

                .specifications-container {
                    background: #f8f9fa;
                    border-radius: 8px;
                    padding: 1rem;
                }

                .specification-item,
                .price-range-item {
                    margin-bottom: 1rem;
                    padding: 1rem;
                    background: #fff;
                    border-radius: 6px;
                    border: 1px solid #e9ecef;
                }

                .specification-inputs {
                    display: flex;
                    gap: 1rem;
                    align-items: flex-start;
                }

                .specification-input {
                    flex: 1;
                }

                .specification-action {
                    display: flex;
                    align-items: center;
                }

                .specification-actions {
                    margin-top: 1rem;
                    padding-top: 1rem;
                    border-top: 1px solid #e9ecef;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    margin-top: 2rem;
                    padding-top: 1rem;
                    border-top: 1px solid #e9ecef;
                }

                @media (max-width: 768px) {
                    .section-container {
                        padding: 1rem;
                    }

                    .section-header {
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                        gap: 0.5rem;
                    }

                    .section-icon {
                        width: 32px;
                        height: 32px;
                    }

                    .section-icon svg {
                        width: 20px;
                        height: 20px;
                    }

                    .specification-item {
                        padding: 0.75rem;
                    }

                    .specification-inputs {
                        flex-direction: column;
                        gap: 0.75rem;
                    }

                    .specification-action {
                        align-self: center;
                    }
                }
                `}
      </style>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            // actions={[{ content: "Kapat", onClick: () => removeToast(index) }]}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(CreateDeviceModel, ["admin", "employee"]);
